<template lang="pug">
.images(v-if="edit" :class="{ 'is-dragging': dragging }" ref="container")
    el-form-item(
        v-if="maxWidth !== undefined"
        :label="$t(config.name)"
        :class="classes"
    )
        .replace-action(v-if="showLibraryAction && libraryActionMode === 'replace'")
            //- el-button.image-library-button.image-library-button--replace(
            //-     plain
            //-     size="small"
            //-     icon="el-icon-picture-outline"
            //-     @click="openLibrary(libraryActionMode)"
            //- ) {{ openLibraryLabel }}
        draggable.list(
            v-if="filteredContents && filteredContents.length"
            v-model="contents"
            :group="`${section.id}:images`"
            draggable="li.draggable"
            filter="input, textarea, .el-input__inner, .ql-editor"
            :prevent-on-filter="false"
            @start="dragging = true"
            @end="dragging = false"
        )
            //- if image is a standalone content element
            transition-group(name="fade" tag="ul" class="no-style-list" v-if="!content")
                li.content.draggable(
                    v-for="content in contents" :key="`${content.id}:${renderNonce}`"
                    v-if="!skip(content)"
                )
                    //- pre {{ content.position }}
                    template(v-if="inline && content.images && content.images.length")
                        include image-cropper.pug
                    template(v-else)
                        a(
                            href="#"
                            @click.prevent="! content.deleted && content.images && content.images.length && openUnifiedEditor(content, content.images[0], 0)"
                            :class="{ deleted: content.deleted }"
                        )
                            include image-tile.pug

            //- if image is part of a complex content

            transition-group(name="fade" tag="ul" class="no-style-list" v-else)
                li.image(
                    v-if="content.images && content.images.length"
                    v-for="(image, imageIndex) in content.images"
                    :key="`${image.id}:${renderNonce}`"
                )
                    a(
                        href="#"
                        @click.prevent="! image.deleted && openUnifiedEditor(content, image, imageIndex)"
                        :class="{ deleted: image.deleted }"
                    )
                        //- include image-tile.pug
                        .image-input(v-if="content.images && content.images.length")
                            image-field(
                                :section="section"
                                :content="content"
                                :config="config.image"
                                :image="image"
                                :height="height"
                                :max-width="maxWidth"
                                :aspect-ratio="aspectRatio"
                                :edit="edit"
                            )
                        .edit-popover
                            i.el-icon-fa-pencil.fas

                        el-button.restore-image(
                            v-if="content.deleted"
                            circle type="info" size="mini"
                            icon="el-icon-fa-trash-restore fas"
                            @click.prevent.stop="restoreImage(image.id)"
                        )
                        el-button.delete-image(
                            v-else-if="isDeletable"
                            circle type="danger" size="mini"
                            icon="el-icon-fa-trash-alt far"
                            @click.prevent.stop="deleteImage(image.id)"
                        )

        .content-image(v-else-if="content && content.images && content.images.length")
            include image-tile.pug

        //- (v-else)

        .dropzone-block
            .image-actions(v-if="showLibraryAction && libraryActionMode !== 'replace'")
                el-button.image-library-button(
                    type="primary"
                    size="medium"
                    icon="el-icon-picture-outline"
                    @click="openLibrary(libraryActionMode)"
                ) {{ openLibraryLabel }}
                .image-actions__hint {{ openLibraryHint }}
            el-upload.plus-box.has-info(
                v-if="! max || imagesCount < max"
                ref="upload"
                drag multiple :action="uploadEndpoint"
                accept="image/jpg, image/jpeg, image/png"
                :before-upload="validateUpload"
                :on-change="upoloadChanged"
                :on-success="uploadSucceeded"
                :on-error="uploadFailed"
                :headers="uploadHeaders"
                :data="uploadData"
                :limit="! max ? null : max"
            )
                i.el-icon-plus
                // i.el-icon-upload.el-icon-fa-cloud-upload-alt.fas
                .el-upload__text(v-html=" ! max || max > 1 ? $t('upload.drop.plural') : $t('upload.drop.singular')")
                .el-upload__tip(slot="tip") {{ $t('upload.validation', { filetypes: 'jpg/png', size: `${maxSize}mb`}) }}

    //- Click-to-open unified editor for gallery / repeated content (slider, photoalbum).
    //- Always mounted; the active watcher re-inits onOpen() on each visible false→true.
    generate-image-edit-drawer(
        :visible.sync="editorDrawer.visible"
        :image="editorDrawer.image"
        :mutable="true"
        :preview-url="editorDrawer.image ? getImageSrc(editorDrawer.image) : null"
        :deferred-crop="true"
        :upload-context="contentImageUploadContext"
        @crop="processing => storeInlineProcessing(editorDrawer.content, processing, editorDrawer.index)"
        @updated="onContentImageEdited"
        @replaced="(img) => onContentImageReplaced(editorDrawer.image, img)"
        @update-image-alt="(id, alt) => onContentImageAltUpdated(editorDrawer.image, alt)"
        @update-image-ai-disclosure="(id, value) => onContentImageAiDisclosureUpdated(editorDrawer.image, value)"
        @update-image-ai-label-display="(id, value) => onContentImageAiLabelDisplayUpdated(editorDrawer.image, value)"
        @update-image-ai-source-type="(id, value) => onContentImageAiSourceTypeUpdated(editorDrawer.image, value)"
    )
        //- The clicked content's own input fields (caption/link/…) — these lived in the
        //- legacy image modal. Slider (repeated contents, !content) edits them here; the
        //- photoalbum host (content prop set) renders its own fields, so gate on !content.
        template(slot="content")
            .gm-content-fields(v-if="!content && editorDrawer.content && config.fields && config.fields.length")
                .field(
                    v-for="field in config.fields"
                    v-if="checkConditions(field)"
                    :key="field.key"
                    :class="{ edit: edit }"
                )
                    input-field(
                        :section="section"
                        :content="editorDrawer.content"
                        :config="field"
                        :options="options"
                        :settings="config.settings"
                        :edit="edit"
                        @update-settings="payload => updateContentSettings(editorDrawer.content, payload)"
                    )

    el-dialog.image-dialog(
        :title="$t('Image settings')"
        :visible.sync="imageModal.visibility"
        :before-close="confirmDiscard(() => imageModal.cropMoved || selectedAspectRatioKey !== 'original')"
        :close-on-click-modal="false"
        :width="modalWidth"
        :append-to-body="true"
        top="4vh"
        @opened="imageModal.loading = false"
        @closed="resetImageModal"
    )
        .image-dialog-actions(v-if="! imageModal.loading")
            el-button(
                v-if="imageModal.form.image"
                plain
                size="mini"
                icon="el-icon-picture-outline"
                @click="openReplaceLibrary"
            ) {{ $t('Replace image') }}
            .aspect-ratio-group(v-if="aspectRatio === null")
                el-dropdown(@command="selectedAspectRatioKey = $event" trigger="click")
                    el-button(size="mini" plain icon="el-icon-fa-crop-alt fas")
                        | {{ $t('Aspect ratio') }} ({{ selectedAspectRatioDisplayLabel }})
                        i.el-icon-arrow-down.el-icon--right
                    el-dropdown-menu(slot="dropdown")
                        el-dropdown-item(
                            v-for="option in aspectRatioOptions"
                            :key="option.key"
                            :command="option.key"
                            :class="{ 'is-selected': selectedAspectRatioKey === option.key }"
                        ) {{ option.label }}
                el-input(
                    v-if="selectedAspectRatioKey === 'custom'"
                    v-model="customAspectRatioInput"
                    size="mini"
                    :placeholder="$t('e.g. 16:9')"
                    style="width: 90px"
                )
        .modal-body(v-if="! imageModal.loading" :class="imageModal.form.orientation" v-loading="! imageModal.form.preview")
            .image-selection-sidebar(v-if="imageModal.form.content.images && imageModal.form.content.images.length > 1")
                // TODO: auf der linken Seite soll das gewünschte Bild ausgewählt werden
            .cropper-box
                .container(:style="{ width: imageModal.form.dimensions.width + 'px' }")
                    // :img-style="{ 'width': imageModal.form.image.width, 'height': imageModal.form.image.height }"
                    vue-cropper(
                        :key="cropperKey"
                        ref="cropper"
                        drag-mode="crop"
                        :src="imageModal.form.src"
                        :ready="handleCropperReady"
                        :cropend="markCropMoved"
                        :rotatable="false"
                        :background="false"
                        :restore="false"
                        :check-orientation="false"
                        :img-style="{ height: imageModal.form.dimensions.height + 'px' }"
                        :view-mode="2"
                        :zoomable="false"
                        :data="imageModal.form.crop"
                        :auto-crop-area="1"
                        :aspect-ratio="effectiveAspectRatio"
                        :crop="debouncedUpdateCrop"
                    )
            .content-sidebar(v-if="imageModal.form.preview")
                .settings(v-if="settings && settings.length")
                    .settings-wrap
                        .setting(v-for="setting in settings" :key="setting.key")
                            input-field(
                                :section="section"
                                :content="contentSettingValues"
                                :options="options"
                                :config="setting"
                                :edit="edit"
                                @change="updateSettings"
                            )
                .alt-text-feature(:class="{ 'alt-text-feature--plain': !altAutoEnabled }")
                    .alt-text-feature__header
                        .alt-text-feature__label
                            i.el-icon-fa-magic.fas
                            span {{ $t('Alternate text for IMG tags') }}
                        .alt-text-feature__meta
                            el-tooltip(
                                v-if="mistralEnabled && altAutoEnabled"
                                :disabled="!altLastTokens"
                                :content="altLastTokens ? `Input: ${altLastTokens.input} · Output: ${altLastTokens.output} Tokens` : ''"
                                placement="top"
                                effect="dark"
                                :append-to-body="true"
                            )
                                span
                                    el-tag.alt-text-feature__badge(size="mini" effect="dark") {{ $t('AI') }}
                            el-popover(placement="right" width="250" trigger="hover")
                                span {{ $t('alt_hint') }}
                                el-button.alt-text-feature__help(slot="reference" circle size="mini") ?
                    el-input.alt-text-feature__input(
                        v-model="imageModal.form.image.alt"
                        :placeholder="imageModal.form.image.description || $t('alt_placeholder')"
                        maxlength="255"
                        show-word-limit
                    )
                    .alt-text-feature__footer(v-if="mistralEnabled && altAutoEnabled")
                        el-tooltip(
                            v-if="altGenerateCount >= altMaxGenerations"
                            :content="$t('alt_limit_hint')"
                            placement="top"
                            effect="dark"
                        )
                            el-button.alt-text-feature__btn(
                                size="small"
                                disabled
                                icon="el-icon-fa-magic fas"
                            ) {{ imageModal.form.image && imageModal.form.image.alt ? $t('Regenerate alt text') : $t('Generate alt text') }}
                        el-button.alt-text-feature__btn(
                            v-else
                            size="small"
                            :loading="altGenerating"
                            icon="el-icon-fa-magic fas"
                            @click="regenerateAlt"
                        ) {{ imageModal.form.image && imageModal.form.image.alt ? $t('Regenerate alt text') : $t('Generate alt text') }}
                        p.alt-text-feature__hint(v-if="altGenerateCount > 0 && altGenerateCount < altMaxGenerations") {{ $tc('alt_remaining_hint', altMaxGenerations - altGenerateCount) }}
                        p.alt-text-feature__hint {{ $t('ai_alt_hint') }}
                template(v-if="config.fields && config.fields.length && !content")
                    .field(
                        v-for="field in config.fields"
                        v-if="checkConditions(field)"
                        :key="field.key"
                        :class="{ edit: edit }"
                    )
                        input-field(
                            :section="section"
                            :content="imageModal.form.content"
                            :config="field"
                            :options="options"
                            :settings="config.settings"
                            :edit="edit"
                            @update-settings="updateSettings"
                        )
        template(slot="footer")
            el-button(
                size="mini"
                icon="el-icon-fa-times fas"
                @click="closeImageModal"
            ) {{ $t('Dismiss') }}
            el-button(
                size="mini"
                type="primary"
                icon="el-icon-fa-check fas"
                @click="acceptImageModal"
            ) {{ $t('Ok') }}

    image-library-dialog(
        :visible.sync="libraryModal.visibility"
        :multiple="libraryAllowsMultiple"
        :selection-limit="librarySelectionLimit"
        @select="selectImageFromLibrary"
    )

.images.preview(v-else ref="container")
    ul.no-style-list(v-if="filteredContents && filteredContents.length && maxWidth !== undefined")
        template(v-if="!content")
            li.content(v-for="content in filteredContents" :key="content.id")
                a(
                    href="#"
                    @click.prevent="! content.deleted && openImageModal(content.id)"
                    :class="{ deleted: content.deleted }"
                )
                    .image(v-if="content.images && content.images.length" v-for="image in content.images")
                        image-field(
                            :section="section"
                            :config="config.image"
                            :content="content"
                            :image="image"
                            :height="height"
                            :max-width="maxWidth"
                            :aspect-ratio="aspectRatio"
                            :edit="false"
                        )
        template(v-else-if="content.images && content.images.length")
            li.image(v-for="image in content.images" :key="image.id")
                image-field(
                    :section="section"
                    :config="config.image"
                    :content="content"
                    :image="image"
                    :height="height"
                    :max-width="maxWidth"
                    :aspect-ratio="aspectRatio"
                    :edit="false"
                )
</template>

<i18n>
{
    "de": {
        "Could not find the content.": "Der Inhalt konnte nicht gefunden werden.",
        "Image settings": "Bild-Einstellungen",
        "Alternate text for IMG tags": "Bildbeschreibung (Alt-Text)",
        "alt_hint": "Der Alternativtext dient der Barrierefreiheit, indem er den Bildinhalt für Screenreader beschreibt und dem Nutzer eine textliche Entsprechung bietet, falls das Bild nicht geladen werden kann.",
        "No images found.": "Es konnten keine Bilder gefunden werden",
        "Open media library": "Mediathek öffnen",
        "Select multiple images": "Mehrere Bilder auswählen",
        "Replace image": "Bild austauschen",
        "Aspect ratio": "Bildseitenverhältnis",
        "Original aspect ratio": "Originales Verhältnis",
        "Custom": "Benutzerdefiniert",
        "e.g. 16:9": "z.B. 16:9",
        "Select image from media library": "Bild aus Mediathek auswählen",
        "Select images from media library": "Bilder aus Mediathek auswählen",
        "Select an existing image from the media library or upload a new one below.": "Ein vorhandenes Bild aus der Mediathek auswählen oder unten ein neues hochladen.",
        "Select existing images from the media library or upload new ones below.": "Vorhandene Bilder aus der Mediathek auswählen oder unten neue hochladen.",
        "Replace the current image with one from the media library.": "Das aktuelle Bild durch eines aus der Mediathek ersetzen.",
        "AI & variants": "KI & Varianten",
        "Generate alt text": "Alt-Text generieren",
        "Regenerate alt text": "Alt-Text neu generieren",
        "Alt text could not be generated.": "Alt-Text konnte nicht generiert werden.",
        "AI": "KI",
        "ai_alt_hint": "KI-generiert (Mistral AI, Europa) · Bitte vor Veröffentlichung prüfen.",
        "alt_placeholder": "Kurze Bildbeschreibung für Screenreader...",
        "alt_limit_hint": "Limit erreicht · Bitte den Text manuell anpassen.",
        "alt_remaining_hint": "Noch 1 Generierung verfügbar | Noch {count} Generierungen verfügbar",
        "alt_generated_notify": "Alt-Text wurde automatisch generiert.",
        "alt_generated_batch": "Bilder wurden automatisch mit Alt-Text versehen.",
        "alt_limit_reached_notify": "Limit erreicht – Alt-Text bitte manuell prüfen."
    },
    "en": {
        "ai_alt_hint": "AI-generated (Mistral AI, Europe) · Please review before publishing.",
        "alt_placeholder": "Short image description for screen readers...",
        "alt_limit_hint": "Limit reached · Please adjust the text manually.",
        "alt_remaining_hint": "1 generation left | {count} generations left",
        "alt_generated_notify": "Alt text was generated automatically.",
        "alt_generated_batch": "images were given alt text automatically.",
        "alt_limit_reached_notify": "Limit reached – please review the alt text manually."
    }
}
</i18n>

<script>

import _ from 'lodash'
import axios from 'axios'

import draggable from 'vuedraggable'
import { useAuthStore, useSetupStore } from '@/stores'

import uniqid from 'uniqid';

import store from '@/store'
import AuthApi from '@/store/api/system/auth';

import {
    set,
    ensureFreshToken,
    addContentToSection,
    sortContents,
} from '@/helpers'

import VueCropper from 'vue-cropperjs'
import ImageField from './ImageField'
import InputField from '../InputField'
import ImageLibraryDialog from '../ImageLibraryDialog'
import GenerateImageEditDrawer from '@/extensions/generative-media/views/Drawers/GenerateImageEditDrawer'
import discardGuard from '@/mixins/discardGuard'

export default {
    name: 'ImageContent',
    mixins: [discardGuard],
    props: {
        section: {
            type: Object,
            required: false
        },
        content: {
            type: Object,
            required: false
        },
        config: {
            type: Object,
            required: true
        },
        options: {
            type: Object,
            required: true
        },
        edit: {
            type: Boolean,
            default: false,
        },
        inline: {
            type: Boolean,
            default: false,
        },
        height: {
            type: Object,
            default: () => {
                return {
                    preview: 80,
                    edit: 150,
                }
            }
        },

        // all setting values as associative object (key: value)
        sectionSettingValues: {
            type: Object,
            default: () => {
                return {}
            }
        },
        sectionSettings: {
            type: Array,
            required: false
        },
    },
    data() {
        return {
            maxWidth: undefined,

            // visibleSettings: false,

            // Click-to-open unified editor (crop + AI + versions + alt) for repeated /
            // gallery content (slider, photoalbum) — the inline editor is reserved for the
            // single-content case. Holds the clicked content/image/index so the shared
            // onContentImage* handlers target the right entry.
            editorDrawer: {
                visible: false,
                content: null,
                image: null,
                index: 0,
            },

            imageModal: {
                visibility: false,
                loading: false,
                // true once the user has dragged the crop box; typed alt text is bound to the
                // image itself and survives a close
                cropMoved: false,
                form: {
                    orientation: undefined,
                    dimensions: {},
                    crop: {},
                    // c: undefined,
                    content: {},
                    image: undefined,
                    src: undefined,
                    preview: undefined,
                    i: 0,
                },
            },

            libraryModal: {
                visibility: false,
                mode: 'add',
            },

            renderNonce: 0,
            dragging: false,
            selectedAspectRatioKey: 'original',
            customAspectRatioInput: '',
            altGenerating: false,
            altGenerateCount: 0,
            altGenerateCounts: {},
            altLastTokens: null,
            altTokensMap: {},
            altBatchPending: 0,
            altBatchCompleted: 0,
        }
    },
    components: {
        ImageField,
        ImageLibraryDialog,
        InputField,
        VueCropper,
        draggable,
        GenerateImageEditDrawer,
    },
    watch: {
        edit() {
            this.calculateMaxWidth()
        },
        effectiveAspectRatio(newVal) {
            if (this._detectingAspectRatio) return
            const val = newVal != null ? newVal : NaN
            // Dialog-Cropper (single ref)
            const dialogCropper = this.getCropperInstance()
            if (dialogCropper) {
                dialogCropper.setAspectRatio(val)
                return
            }
            // Inline-Croppers (array ref aus v-for)
            const refs = this.$refs.cropper
            if (Array.isArray(refs)) {
                refs.forEach(c => { if (c && c.cropper) c.cropper.setAspectRatio(val) })
            }
        },
    },
    computed: {
        uploadHeaders() {
            return {
                authorization: `Bearer ${useAuthStore().token}`,
                accept: 'application/json',
            }
        },
        id() {
            // return _.get(this.form, 'id', false)
        },
        randomId() {
            return uniqid()
        },
        imagesCount() {
            let count = 0;
            let contentImages;

            if (this.content) {
                contentImages = _.get(this.content, 'images') || [];
            } else {
                contentImages = this.filteredContents || [];
            }

            if (contentImages.length && contentImages.length > 0) count = contentImages.length;
            return count;
        },
        isDeletable() {
            if (!this.content) {
                const contents = this.filteredContents.filter(el => ! el.deleted)
                return this.min === null || this.min < contents.length
            } else {
                const images = this.content.images.filter(el => ! el.deleted)
                return this.min === null || this.min < images.length
            }
        },
        aspectRatio() {
            let aspectRatio = false;
            if (this.config) {
                aspectRatio = _.get(this.config, 'image.aspectRatio', false);
            }
            if (!aspectRatio && this.config && this.config.fields) {
                aspectRatio = _.get(this.config.fields.find(field => {
                    return field.key === 'images'
                }), 'aspectRatio', false);
            }
            let result = null;
            if (aspectRatio) {
                if (this.checkConditions(aspectRatio)) {
                    const w = _.get(aspectRatio, 'width')
                    const h = _.get(aspectRatio, 'height')

                    if (w && h) result = w / h
                }
            } else {
                let aspectRatios = false;
                if (this.config) {
                    aspectRatios = _.get(this.config, 'image.aspectRatios', false);
                }
                if (!aspectRatios && this.config && this.config.fields) {
                    aspectRatios = _.get(this.config.fields.find(field => {
                        return field.key === 'images'
                    }), 'aspectRatios', false);
                }
                if (aspectRatios && aspectRatios.length) {
                    aspectRatios.forEach(ratio => {
                        if (ratio && this.checkConditions(ratio)) {
                            const w = _.get(ratio, 'width')
                            const h = _.get(ratio, 'height')

                            if (w && h) result = w / h
                        }
                    })
                }
            }

            return result;
        },
        aspectRatioOptions() {
            return [
                { key: 'original', label: this.$t('Original aspect ratio') },
                { key: '1:1', label: '1:1' },
                { key: '4:3', label: '4:3' },
                { key: '3:2', label: '3:2' },
                { key: '16:9', label: '16:9' },
                { key: '2:3', label: '2:3' },
                { key: '3:4', label: '3:4' },
                { key: '9:16', label: '9:16' },
                { key: 'custom', label: this.$t('Custom') },
            ]
        },
        effectiveAspectRatio() {
            if (this.selectedAspectRatioKey === 'original') {
                return this.aspectRatio
            }
            if (this.selectedAspectRatioKey === 'custom') {
                if (!this.customAspectRatioInput) return this.aspectRatio
                const parts = this.customAspectRatioInput.split(':').map(s => parseFloat(s.trim()))
                if (parts.length === 2 && parts[0] > 0 && parts[1] > 0) return parts[0] / parts[1]
                const num = parseFloat(this.customAspectRatioInput)
                return (!isNaN(num) && num > 0) ? num : this.aspectRatio
            }
            const [w, h] = this.selectedAspectRatioKey.split(':').map(Number)
            return (w && h) ? w / h : this.aspectRatio
        },
        selectedAspectRatioDisplayLabel() {
            if (this.selectedAspectRatioKey === 'original') {
                const ratio = this.aspectRatio
                if (!ratio) return 'Original'
                const match = this.aspectRatioOptions.find(o => {
                    if (o.key === 'original' || o.key === 'custom') return false
                    const [w, h] = o.key.split(':').map(Number)
                    return w && h && Math.abs((w / h) - ratio) < 0.001
                })
                return match ? match.key : 'Original'
            }
            if (this.selectedAspectRatioKey === 'custom') {
                return this.customAspectRatioInput || this.$t('Custom')
            }
            return this.selectedAspectRatioKey
        },
        classes() {
            // { 'inline-cropper': inline, 'flex': (!content && filteredContents.length) || (content && content.images && content.images.length) }

            return {
                'inline-cropper': this.inline,
                'flex': (!this.content && this.filteredContents.length) || (this.content && this.content.images && this.content.images.length),
            }
        },
        modalWidth() {
            let width = 540

            // // image is content by itself
            // if (this.c === undefined || this.c === null || this.c === false) {
            //     if ((this.config.fields && this.config.fields.length)
            //         || (this.settings && this.settings.length)
            //         || this.imageModal.form.orientation === 'landscape'
            //     ) {
            //         width = 1200
            //     } else if (this.imageModal.form.dimensions && this.imageModal.form.dimensions.width) {
            //         width = this.imageModal.form.dimensions.width + 80
            //     }
            // } else {
            //     if ((this.settings && this.settings.length)
            //         || this.imageModal.form.orientation === 'landscape'
            //     ) {
            //         width = 1200
            //     } else if (this.imageModal.form.dimensions && this.imageModal.form.dimensions.width) {
            //         width = this.imageModal.form.dimensions.width + 80
            //     }
            // }

            // image is content by itself
            if (!this.content) {
                if ((this.config.fields && this.config.fields.length)
                    || (this.settings && this.settings.length)
                    || this.imageModal.form.orientation === 'landscape'
                ) {
                    width = 1200
                } else if (this.imageModal.form.dimensions && this.imageModal.form.dimensions.width) {
                    // add 380 px for the alt input field
                    width = this.imageModal.form.dimensions.width + 80 + 380 + 40
                }
            } else {
                if ((this.settings && this.settings.length)
                    || this.imageModal.form.orientation === 'landscape'
                ) {
                    width = 1200
                } else if (this.imageModal.form.dimensions && this.imageModal.form.dimensions.width) {
                    // add 380 px for the alt input field
                    width = this.imageModal.form.dimensions.width + 80 + 380 + 40
                }
            }

            return `${width}px`
        },
        contents: {
            get() {
                const contents = _.get(this.section, 'contents') || []

                return contents

                    // NOTE: sort creates infinite loop
                    // .sort((a, b) => _.get(a, 'position', 0) - _.get(b, 'position', 0))

                    // NOTE: filter breaks the draggable list
                    // .filter(el => el.key === this.config.key)
            },
            set(sorted) {
                if (!this.content) {
                    let contents = _.get(this.section, 'contents') || []
                    contents.forEach(content => {
                        const i = sorted.findIndex(el => el.id === content.id)
                        content.position = i + 1
                    });
                    sortContents(contents)
                } else {
                    // TODO: sort content.images ?
                }
            }
        },
        filteredContents() {
            return _.cloneDeep(this.contents)
                .filter(el => el.key === this.config.key)
        },
        actualFilteredContents() {
            return (this.contents || [])
                .filter(el => el.key === this.config.key)
        },
        min() {
            let min
            if (!this.content) min = _.get(this.config, 'min')
            else min = _.get(this.config, 'image.min')
            return min
        },
        max() {
            let max
            if (!this.content) max = _.get(this.config, 'max')
            else max = _.get(this.config, 'image.max')
            return max
        },
        librarySelectionLimit() {
            if (this.libraryModal.mode !== 'add') return 1

            if (this.content) {
                const imageMax = _.get(this.config, 'image.max')
                const sectionMax = _.get(this.config, 'max')

                const remainingInContent = imageMax != null
                    ? Math.max(imageMax - this.imagesCount, 0)
                    : null

                const remainingNewContent = sectionMax != null
                    ? Math.max(sectionMax - this.filteredContents.length, 0)
                    : null

                // No section max → unlimited new content items can be created
                if (remainingNewContent === null) return null
                if (remainingInContent === null) return remainingNewContent
                return remainingInContent + remainingNewContent
            }

            if (! this.max) return null
            return Math.max(this.max - this.imagesCount, 0)
        },
        libraryAllowsMultiple() {
            return this.libraryModal.mode === 'add'
                && (this.librarySelectionLimit === null || this.librarySelectionLimit > 1)
        },
        canReplaceFromLibrary() {
            if (this.content) {
                return _.get(this.config, 'image.max') !== null
                    && _.get(this.config, 'image.max') !== undefined
                    && Number(_.get(this.config, 'image.max')) === 1
                    && (_.get(this.content, 'images', []) || []).filter(image => !image.deleted).length === 1
            }

            return this.max !== null
                && this.max !== undefined
                && Number(this.max) === 1
                && this.actualFilteredContents.filter(content => !content.deleted).length === 1
                && (_.get(this.actualFilteredContents.find(content => !content.deleted), 'images', []) || []).filter(image => !image.deleted).length === 1
        },
        showLibraryAction() {
            return this.canReplaceFromLibrary || !this.max || this.imagesCount < this.max
        },
        libraryActionMode() {
            return this.canReplaceFromLibrary ? 'replace' : 'add'
        },
        openLibraryLabel() {
            if (this.libraryActionMode === 'replace') return this.$t('Replace image')
            return this.libraryAllowsMultiple
                ? this.$t('Select images from media library')
                : this.$t('Select image from media library')
        },
        openLibraryHint() {
            if (this.libraryActionMode === 'replace') {
                return this.$t('Replace the current image with one from the media library.')
            }

            return this.libraryAllowsMultiple
                ? this.$t('Select existing images from the media library or upload new ones below.')
                : this.$t('Select an existing image from the media library or upload a new one below.')
        },
        cropperKey() {
            const imageId = _.get(this.imageModal.form, 'image.id')
            const sourceId = _.get(this.imageModal.form, 'image.source.id')
            const src = _.get(this.imageModal.form, 'src', '')

            return [imageId, sourceId, src].filter(Boolean).join(':')
        },
        maxSize() {
            return _.get(this.config, 'image.maxSize') || 12
        },
        mistralEnabled() {
            return !!process.env.VUE_APP_MISTRAL_ALT_API_KEY
                && process.env.VUE_APP_MISTRAL_ALT_ENABLED !== 'false'
        },
        aiImageEditEnabled() {
            const extensions = _.get(this.setupStore.activeCms, 'extensions', []) || []
            return !!extensions.find(extension => extension.key === 'ai-image-edit')
        },
        // True when the inline editor for the (single-content) image is the unified
        // editor — used to hide the legacy aspect-ratio dropdown, since the unified
        // editor provides its own in-canvas aspect buttons.
        unifiedEditorForFirstImage() {
            const contents = this.content ? [this.content] : (this.contents || [])
            for (const content of contents) {
                const image = _.get(content, 'images.0')
                if (image) return this.useUnifiedEditor(image)
            }
            return false
        },
        altAutoEnabled() {
            return _.get(this.setupStore.settingValues, 'altAutoGenerate', true) !== false
        },
        altAutoTriggered() {
            return this.altAutoEnabled && _.get(this.setupStore.settingValues, 'altAutoTrigger', true) !== false
        },
        altMaxGenerations() {
            return parseInt(process.env.VUE_APP_MISTRAL_ALT_MAX_ATTEMPTS || '5', 10)
        },
        settings() {
            let settings = _.get(this.config, 'settings') || []
            settings = settings.filter(setting => setting.field === 'image')
            return settings
        },
        contentSettingValues() {
            // TODO: check if this is correctly implemented
            // see src/components/Page/Sections/Partials/Field.js
            let settingValues = _.cloneDeep(this.sectionSettingValues)
            let options = {}

            // get default values
            if (this.settings && this.settings.length) {
                this.settings.forEach(setting => {
                    if (setting.options && setting.options.length) {
                        // const option = setting.options.find(option => option.default === true)
                        setting.options.forEach(option => {
                            if (option.default === true) settingValues[setting.key] = option.key
                            _.set(options, `${setting.key}.${option.key}`, option.value)
                        })
                    }
                })
            }

            const content = this.imageModal.form.content

            // override with actual values
            if (content.settings && content.settings.length) {
                content.settings.forEach(setting => {
                    // check if selected option exists
                    if (options[setting.key][setting.value] !== undefined) {
                        settingValues[setting.key] = setting.value
                    }
                })
            }

            return settingValues
        },
        uploadData() {
            let uploadData = {
                'context[type]': 'content-image'
            }
            return uploadData
        },
        // form() {
        //     return store.getters[`${this.module}/form`]
        // },
        // ui() {
        //     return store.getters[`${this.module}/ui`]
        // },
        uploadEndpoint() {
            return `${this.setupStore.uploadEndpoint}/public/${this.activeCms.id}/content`
        },
        // Lets the unified editor's own Replace/Upload control post to the content
        // upload endpoint with the content-image context.
        contentImageUploadContext() {
            return { endpoint: this.uploadEndpoint, type: 'content-image' }
        },
        setupStore() {
            return useSetupStore()
        },
        activeCms() {
            return this.setupStore.activeCms
        },
        enabledExtensions() {
            return this.setupStore.enabledExtensions
        },
    },
    methods: {
        bumpRenderNonce() {
            this.renderNonce += 1
        },
        getDefaultImageAlt(image, fallbackImage = null) {
            const candidates = [
                _.get(image, 'alt'),
                _.get(fallbackImage, 'alt'),
            ]

            const alt = candidates.find(value => typeof value === 'string' && value.trim())
            return alt ? alt.trim() : ''
        },
        applyDefaultImageAlt(image, fallbackImage = null) {
            if (! image) return image

            if (_.trim(_.get(image, 'alt', ''))) return image

            const alt = this.getDefaultImageAlt(image, fallbackImage)
            if (alt) image.alt = alt

            return image
        },
        skip(content) {
            return !this.filteredContents.some(el => el.id === content.id)
        },
        checkConditions(field) {
            let valid = true

            // eslint-disable-next-line no-unused-vars
            const settings = this.contentSettingValues;
            const enabledExtensions = this.enabledExtensions;
            try {
                const conditions = field.conditions
                if (conditions && conditions.length) {
                    conditions.forEach(o => {
                        if (valid === false) return

                        const { type, condition } = o
                        switch (type) {
                            case 'eval':
                                valid = eval(condition)
                                break;

                            default:
                                valid = false
                        }
                    })
                }
            } catch (error) {
                // eslint-disable-next-line
                console.error(error);
                valid = false
            }

            return valid
        },
        getImageSrc(image) {
            // const storageRoute = _.trim(`${process.env.VUE_APP_API_ENDPOINT}${process.env.VUE_APP_API_PRIVATE_STORAGE_ROUTE}`, '/')
            const storageRoute = _.trim(`${process.env.VUE_APP_API_ENDPOINT}${process.env.VUE_APP_API_PUBLIC_STORAGE_ROUTE}`, '/')
            // processedFile FIRST: it is the authoritative pointer to the layer the image
            // currently renders (an AI edit, a crop). Reading url/source only meant an
            // applied edit stayed invisible in the CMS editor.
            const sourceUrl = _.get(image, 'processedFile.url')
                || _.get(image, 'url')
                || _.get(image, 'source.url')

            let table = 'images'
            if (image.upload) table = 'uploads'

            // const token = AuthApi.getToken()
            // const src = `${storageRoute}/${table}/${image.id}/${image.name}?token=${token}`
            return sourceUrl || `${storageRoute}/${table}/${image.id}/${image.name}`
        },
        getImageCrop(image) {
            let crop = {}

            const processing = _.get(image, 'settings.processing')
            if (processing) {
                // let { fx, fy, fz, w, h } = parameters

                let fx = _.get(processing, 'x', 0.5)
                let fy = _.get(processing, 'y', 0.5)
                let fz = _.get(processing, 'z', 1)
                let w = _.get(processing, 'w')
                let h = _.get(processing, 'h')

                let cropAspectRatio = this.aspectRatio
                if (! cropAspectRatio && w && h) {
                    w = parseFloat(w)
                    h = parseFloat(h)

                    cropAspectRatio = w / h
                }

                if (! cropAspectRatio) {
                    cropAspectRatio = image.width / image.height
                }

                if (fx && fy && fz && cropAspectRatio) {
                    fx = parseFloat(fx)
                    fy = parseFloat(fy)
                    fz = 1 / parseFloat(fz)

                    const maxWidth = image.width * fz
                    const maxHeight = image.height * fz

                    // if original aspect ratio is larger than the allowed
                    // then the cropped area will be limited by its height
                    if (image.width / image.height > cropAspectRatio) {
                        crop.width = maxHeight * cropAspectRatio
                        crop.height = maxHeight
                    }

                    // if original aspect ratio is smaller than the allowed
                    // then the cropped area will be limited by its width
                    else if (image.width / image.height < cropAspectRatio) {
                        crop.width = maxWidth
                        crop.height = maxWidth / cropAspectRatio
                    }

                    // if original aspect ratio is equal to the allowed
                    // then the cropped area will have the same proportions
                    else {
                        crop.width = maxWidth
                        crop.height = maxHeight
                    }
                }

                crop.x = (image.width * fx) - (crop.width / 2)
                crop.y = (image.height * fy) - (crop.height / 2)
            }

            return crop
        },
        getProcessingObjectFromCropperData(data, originalWidth, originalHeight) {
            // calculate focal point
            const { width, height, x, y } = data

            const originalAspectRatio = originalWidth / originalHeight

            const requestedAspectRatio = width / height

            let fx, fy, fz

            fx =  (x + width / 2) / originalWidth
            fy =  (y + height / 2) / originalHeight

            // if original aspect ratio is larger than the requested
            // then z (zoom) must be calculated by height
            if (originalAspectRatio > requestedAspectRatio) {
                fz = originalHeight / height
            }

            // if original aspect ratio is smaller than the requested
            // then z (zoom) must be calculated by width
            else if (originalAspectRatio < requestedAspectRatio) {
                fz = originalWidth / width
            }

            // if original aspect ratio is equal to the requested
            // then z (zoom) can be calculated by width or height
            else {
                fz = originalWidth / width
            }

            // return `fit=crop&crop=focalpoint&x=${fx}&y=${fy}&z=${fz}&w=${width}&h=${height}`

            return {
                fit: 'crop',
                crop: 'focalpoint',
                x: fx,
                y: fy,
                z: fz,
                w: width,
                h: height,
            }
        },
        getContentIndex(id) {
            // let contents = []
            // if (this.module === 'menu' && this.i >= 0) {
            //     contents = _.get(this.form.menuItems[this.i].page.sections[this.s], 'contents', [])
            // } else if (this.module === 'categoryPage') {
            //     contents = _.get(this.form.sections[this.s], 'contents', [])
            // }

            const contents = _.get(this.section, 'contents') || []
            return _.findIndex(contents, { id })
        },
        deleteContent(id) {
            // const c = this.getContentIndex(id)
            // if (c !== null) {
            //     store.commit(`${this.module}/form/markContentAsDeleted`, { i: this.i, s: this.s, c })
            // }

            if (!this.content) {
                let contents = _.get(this.section, 'contents') || [];
                const i = contents.findIndex(content => content.id === id);
                // contents.splice(i, 1);
                this.$delete(contents, i);
            }
        },
        restoreContent(id) {
            // const c = this.getContentIndex(id)
            // if (c !== null) {
            //     store.commit(`${this.module}/form/unmarkContentAsDeleted`, { i: this.i, s: this.s, c })
            // }
        },
        getImageIndex(id) {
            // let images = []
            // if (this.module === 'menu' && this.i >= 0) {
            //     images = _.get(this.form.menuItems[this.i].page.sections[this.s].contents[this.c], 'images', [])
            // } else if (this.module === 'categoryPage') {
            //     images = _.get(this.form.sections[this.s].contents[this.c], 'images', [])
            // }

            // return _.findIndex(images, { id })
        },
        deleteImage(id) {
            // const img = this.getImageIndex(id)
            // if (img !== null) {
            //     store.commit(`${this.module}/form/markContentImageAsDeleted`, { i: this.i, s: this.s, c: this.c, img })
            // }

            if (this.content) {
                let contentImages = _.get(this.content, 'images') || [];
                const i = contentImages.findIndex(image => image.id === id);
                // contentImages.splice(i, 1);
                this.$delete(contentImages, i);
            }
            localStorage.removeItem(`alt-autogenerated-${id}`)
            localStorage.removeItem(`mistral:alt:${id}`)
        },
        openLibrary(mode = 'add') {
            this.libraryModal.mode = typeof mode === 'string' ? mode : 'add'
            this.libraryModal.visibility = true
        },
        openReplaceLibrary() {
            this.openLibrary('replace')
        },
        // A persisted Image (real id) can host concrete variants; freshly added
        // images carry a client 'new:' id until the page is saved.
        isPersistedImage(image) {
            const id = _.get(image, 'id')
            return !!id && !String(id).startsWith('new:')
        },
        // The unified editor (canvas + crop + AI + version stack) is the single image
        // editing surface. It works for both DB-loaded images (server variants) and
        // freshly-uploaded files (uploads table): the canvas renders from the upload
        // URL, crop is deferred into settings.processing (applied on page save), and AI
        // sources from the upload — no Image is persisted before the page is saved.
        useUnifiedEditor(image) {
            return this.aiImageEditEnabled && !!_.get(image, 'id')
        },
        findContentImageById(imageId) {
            const contents = this.content ? [this.content] : (_.get(this.section, 'contents') || [])
            for (const content of contents) {
                const images = _.get(content, 'images') || []
                const index = images.findIndex(image => String(image.id) === String(imageId))
                if (index >= 0) {
                    return { content, image: images[index], index }
                }
            }
            return { content: null, image: null, index: -1 }
        },
        // The unified editor's shared AltTextField emitted a new alt — persist it onto
        // the content image (the editor edits a clone). The page save writes it.
        onContentImageAltUpdated(image, alt) {
            if (image) this.$set(image, 'alt', alt)
        },
        // Art. 50(4) disclosure decisions, persisted the same way alt is: onto the
        // host's image object so the surrounding Save carries them.
        onContentImageAiDisclosureUpdated(image, disclose) {
            if (image) this.$set(image, 'ai_disclosure', disclose === true)
        },
        onContentImageAiLabelDisplayUpdated(image, mode) {
            if (image) this.$set(image, 'ai_label_display', mode || null)
        },
        onContentImageAiSourceTypeUpdated(image, sourceType) {
            if (image) this.$set(image, 'ai_source_type', sourceType || null)
        },
        // The editor replaced the image (uploaded a new file or picked one from the
        // library). Merge the new identity into the existing content image so the page
        // save persists it; keep `type`/`position` (not part of the editor's payload).
        onContentImageReplaced(image, newImage) {
            if (!image || !newImage) return
            const keys = ['id', 'source', 'url', 'name', 'hash', 'mime', 'size', 'width', 'height', 'alt', 'settings', 'processing_query_string', 'processedFile', 'activeVariant', 'variants', 'ai_disclosure', 'ai_source_type', 'ai_label_display', 'ai_assessment']
            keys.forEach(key => this.$set(image, key, _.get(newImage, key)))
            // A fresh upload nests `source.upload`; a library pick has a persisted source.
            this.$set(image, 'upload', !!(_.get(newImage, 'source.upload') || _.get(newImage, 'upload')))
        },
        onContentImageEdited(updatedImage, previousId = null) {
            if (!updatedImage) return
            // Fresh-upload AI edit: the upload was promoted server-side into a real
            // Image carrying the full non-destructive variant stack (Original + the
            // AI-Image variant + any folded crop). Locate the old upload by its
            // previous id and adopt the persisted Image's identity wholesale, then
            // CLEAR the `upload` flag — otherwise the page-save's "existing images"
            // sync excludes it (it filters out `upload`-flagged entries) and it is
            // routed to the uploads branch with a non-existent upload id and lost.
            if (previousId != null) {
                const target = this.findContentImageById(previousId)
                if (target.image) {
                    const adoptKeys = [
                        'id', 'name', 'preferred_name', 'url', 'hash', 'size', 'mime', 'path', 'disk', 'visibility',
                        'width', 'height', 'alt', 'type', 'position',
                        'source', 'processedFile', 'activeVariant', 'variants', 'settings', 'processing_query_string',
                    ]
                    adoptKeys.forEach(key => this.$set(target.image, key, _.get(updatedImage, key)))
                    this.$set(target.image, 'upload', false)
                }
                return
            }
            // Variant-relevant fields the storefront/search and the cropper read. These
            // are $set reactively so the tile + editor update IN PLACE — no renderNonce
            // bump (that re-keys the content <li> and remounts the editor → flicker).
            const keys = ['processing_query_string', 'processedFile', 'activeVariant', 'variants', 'settings', 'source', 'width', 'height']
            const modalImage = _.get(this.imageModal.form, 'image')
            if (modalImage) {
                keys.forEach(key => this.$set(modalImage, key, _.get(updatedImage, key)))
            }
            const found = this.findContentImageById(_.get(updatedImage, 'id'))
            if (found.image) {
                keys.forEach(key => this.$set(found.image, key, _.get(updatedImage, key)))
            }
        },
        getLibraryReplacementTarget() {
            const modalImage = _.get(this.imageModal.form, `content.images.${this.imageModal.form.i}`) || this.imageModal.form.image
            if (modalImage && this.imageModal.form.content) {
                const actualContent = this.content
                    || (_.get(this.section, 'contents', []) || []).find(content => String(content.id) === String(this.imageModal.form.content.id))
                    || this.imageModal.form.content
                const actualImages = _.get(actualContent, 'images', []) || []
                const actualIndex = actualImages.findIndex(image => String(image.id) === String(modalImage.id))

                return {
                    actualContent,
                    currentImage: actualIndex >= 0 ? actualImages[actualIndex] : modalImage,
                    actualIndex,
                    modalIndex: this.imageModal.form.i || 0,
                    shouldSyncModal: true,
                }
            }

            if (this.content) {
                const activeImages = (_.get(this.content, 'images', []) || []).filter(image => !image.deleted)
                if (Number(_.get(this.config, 'image.max')) !== 1 || activeImages.length !== 1) return null

                const image = activeImages[0]
                return {
                    actualContent: this.content,
                    currentImage: image,
                    actualIndex: this.content.images.findIndex(entry => String(entry.id) === String(image.id)),
                    modalIndex: 0,
                    shouldSyncModal: false,
                }
            }

            const activeContents = this.actualFilteredContents.filter(content => !content.deleted)
            if (Number(this.max) !== 1 || activeContents.length !== 1) return null

            const content = activeContents[0]
            const activeImages = (_.get(content, 'images', []) || []).filter(image => !image.deleted)
            if (activeImages.length !== 1) return null

            const image = activeImages[0]
            return {
                actualContent: content,
                currentImage: image,
                actualIndex: content.images.findIndex(entry => String(entry.id) === String(image.id)),
                modalIndex: 0,
                shouldSyncModal: false,
            }
        },
        getImageDuplicateKey(image) {
            const sourceId = _.get(image, 'source.id')
            if (sourceId !== undefined && sourceId !== null && sourceId !== '') {
                return `source:${sourceId}`
            }

            const imageId = _.get(image, 'id')
            if (imageId !== undefined && imageId !== null && imageId !== '') {
                return `image:${imageId}`
            }

            const imageUrl = _.get(image, 'url')
            if (imageUrl) {
                return `url:${imageUrl}`
            }

            return null
        },
        filterDuplicateImages(images, existingImages = []) {
            const seen = new Set(
                existingImages
                    .map(image => this.getImageDuplicateKey(image))
                    .filter(Boolean)
            )

            return images.filter(image => {
                const key = this.getImageDuplicateKey(image)
                if (! key) return true
                if (seen.has(key)) return false

                seen.add(key)
                return true
            })
        },
        selectImageFromLibrary(image) {
            const selectedImages = Array.isArray(image) ? image : [image]
            const replacementTarget = this.getLibraryReplacementTarget()

            if (this.libraryModal.mode === 'replace' && replacementTarget) {
                this.replaceImageFromLibrary(selectedImages[0], replacementTarget)
                this.libraryModal.mode = 'add'
                return
            }

            if (this.content) {
                if (! this.content.images) this.$set(this.content, 'images', [])

                const uniqueSelectedImages = this.filterDuplicateImages(selectedImages, this.content.images)
                if (! uniqueSelectedImages.length) {
                    this.libraryModal.mode = 'add'
                    return
                }

                const imageMax = _.get(this.config, 'image.max')
                const remainingInContent = imageMax != null
                    ? Math.max(imageMax - this.content.images.length, 0)
                    : uniqueSelectedImages.length

                let position = Math.max(...this.content.images.map(o => _.get(o, 'position', 0) || 0), 0)

                const toAddToContent = uniqueSelectedImages.slice(0, remainingInContent)
                const toCreateNewContent = uniqueSelectedImages.slice(remainingInContent)

                toAddToContent.forEach(selectedImage => {
                    const nextImage = _.cloneDeep(selectedImage)
                    position += 1
                    nextImage.type = 'content-image'
                    nextImage.position = position
                    this.applyDefaultImageAlt(nextImage)
                    this.content.images.push(nextImage)

                    if (this.mistralEnabled && this.altAutoTriggered && !_.trim(nextImage.alt)) {
                        const pushed = this.content.images[this.content.images.length - 1]
                        this.$set(pushed, '_altLoading', true)
                        this.altBatchPending += 1
                        this.generateAltTextWithMistral(pushed)
                            .then(alt => { if (alt) { this.$set(pushed, 'alt', alt); if (this.altLastTokens) this.$set(this.altTokensMap, pushed.id, this.altLastTokens); this.notifyAltBatchDone(pushed.preferred_name || pushed.name) } else { this.altBatchPending -= 1 } })
                            .catch(() => { this.altBatchPending -= 1 })
                            .finally(() => { this.$delete(pushed, '_altLoading') })
                    }
                })

                if (toCreateNewContent.length > 0) {
                    toCreateNewContent.forEach((selectedImage, index) => {
                        const nextImage = _.cloneDeep(selectedImage)
                        nextImage.type = 'content-image'
                        this.applyDefaultImageAlt(nextImage)

                        const content = {
                            key: this.config.key,
                            position: Date.now() + index + 1,
                            images: [nextImage],
                        }

                        addContentToSection(this.section, this.config, content)

                        if (this.mistralEnabled && this.altAutoTriggered && !_.trim(nextImage.alt)) {
                            this.$set(nextImage, '_altLoading', true)
                            this.altBatchPending += 1
                            this.generateAltTextWithMistral(nextImage)
                                .then(alt => { if (alt) { this.$set(nextImage, 'alt', alt); this.notifyAltBatchDone(nextImage.preferred_name || nextImage.name) } else { this.altBatchPending -= 1 } })
                                .catch(() => { this.altBatchPending -= 1 })
                                .finally(() => { this.$delete(nextImage, '_altLoading') })
                        }
                    })

                    this.$nextTick(() => {
                        const contents = _.get(this.section, 'contents') || []
                        sortContents(contents)
                    })
                }

                this.libraryModal.mode = 'add'
                return
            }

            const existingContentImages = this.filteredContents
                .flatMap(content => _.get(content, 'images', []))
            const uniqueSelectedImages = this.filterDuplicateImages(selectedImages, existingContentImages)

            if (! uniqueSelectedImages.length) {
                this.libraryModal.mode = 'add'
                return
            }

            uniqueSelectedImages.forEach((selectedImage, index) => {
                const nextImage = _.cloneDeep(selectedImage)
                nextImage.type = 'content-image'
                this.applyDefaultImageAlt(nextImage)

                const content = {
                    key: this.config.key,
                    position: Date.now() + index,
                    images: [ nextImage ],
                }

                addContentToSection(this.section, this.config, content)

                if (this.mistralEnabled && this.altAutoTriggered && !_.trim(nextImage.alt)) {
                    this.$set(nextImage, '_altLoading', true)
                    this.generateAltTextWithMistral(nextImage)
                        .then(alt => { if (alt) this.$set(nextImage, 'alt', alt) })
                        .catch(() => {})
                        .finally(() => { this.$delete(nextImage, '_altLoading') })
                }
            })

            this.$nextTick(() => {
                const contents = _.get(this.section, 'contents') || []
                sortContents(contents)
            })

            this.libraryModal.mode = 'add'
        },
        buildImageModalForm(content, image, i = 0) {
            this.applyDefaultImageAlt(image)

            const orientation = (image.width / image.height) < 3 ? 'portrait' : 'landscape'

            const padding = 40
            const modalWidth = _.min([1200 - padding, window.innerWidth * .9 - padding])
            const modalHeight = _.min([window.innerHeight * .8 - padding - 100, 500])

            let availibleWidth = modalWidth
            if (orientation === 'portrait') availibleWidth -= modalWidth / 3

            const widthRatio = availibleWidth / image.width
            const heightRatio = modalHeight / image.height

            const ratio = _.min([widthRatio, heightRatio])

            let width = image.width
            let height = image.height

            if (ratio < 1) {
                width *= ratio
                height *= ratio
            }

            if (orientation === 'portrait' && width > 700) {
                const ar = width / height

                width = 700
                height = width / ar
            }

            return {
                orientation,
                dimensions: { width, height },
                crop: this.getImageCrop(image),
                content,
                image,
                src: this.getImageSrc(image),
                preview: undefined,
                i,
            }
        },
        applyImageModalForm(form) {
            this.imageModal.loading = true
            this.imageModal.form = form

            this.$nextTick(() => {
                this.imageModal.loading = false

                this.$nextTick(() => {
                    this.refreshCropperPreview()
                })
            })
        },
        refreshCropperPreview() {
            this.$nextTick(() => {
                this.updateCrop()
            })
        },
        getCropperInstance() {
            return _.get(this.$refs, 'cropper.cropper') || null
        },
        handleCropperReady() {
            this.$nextTick(() => {
                this.detectAspectRatioFromCropper()
                this.updateCrop()
            })
        },
        detectAspectRatioFromCropper() {
            let cropData = null
            const dialogCropper = this.getCropperInstance()
            if (dialogCropper) {
                cropData = dialogCropper.getData()
            } else {
                const refs = this.$refs.cropper
                if (Array.isArray(refs) && refs.length) {
                    const first = refs[0]
                    if (first && first.cropper) cropData = first.cropper.getData()
                }
            }
            if (!cropData || !cropData.width || !cropData.height) return
            const ratio = cropData.width / cropData.height
            const match = this.aspectRatioOptions.find(o => {
                if (o.key === 'original' || o.key === 'custom') return false
                const [w, h] = o.key.split(':').map(Number)
                return w && h && Math.abs((w / h) - ratio) < 0.01
            })
            if (match) {
                this._detectingAspectRatio = true
                this.selectedAspectRatioKey = match.key
                this.$nextTick(() => { this._detectingAspectRatio = false })
            }
        },
        buildReplacementImage(image, currentImage) {
            const replacement = _.cloneDeep(image)
            const currentSettings = _.omit(_.get(currentImage, 'settings', {}), ['processing'])

            replacement.type = 'content-image'
            replacement.position = _.get(currentImage, 'position', 0)
            replacement.alt = _.get(currentImage, 'alt', '')
            this.applyDefaultImageAlt(replacement, currentImage)

            if (!_.isEmpty(currentSettings)) {
                replacement.settings = currentSettings
            } else {
                delete replacement.settings
            }

            delete replacement.upload
            delete replacement.deleted
            delete replacement.files
            delete replacement.srcset
            // A picked VERSION (crop / AI edit) brings its own pointer + stack — keep those
            // or the replacement silently falls back to the un-edited original. A plain file
            // pick carries none, so this still clears the image being replaced.
            if (!replacement.processing_query_string) {
                delete replacement.processedFile
                delete replacement.processing_query_string
                delete replacement.variants
                delete replacement.activeVariant
            }

            return replacement
        },
        syncReplacementImage(targetImage, image) {
            const replacement = this.buildReplacementImage(image, targetImage)

            Object.keys(targetImage).forEach(key => {
                if (replacement[key] === undefined) {
                    this.$delete(targetImage, key)
                }
            })

            Object.keys(replacement).forEach(key => {
                this.$set(targetImage, key, replacement[key])
            })

            return targetImage
        },
        replaceImageFromLibrary(image, replacementTarget = null) {
            const target = replacementTarget || this.getLibraryReplacementTarget()
            if (! target || ! target.currentImage || ! target.actualContent) return

            const replacement = this.buildReplacementImage(image, target.currentImage)
            const actualImages = [ ...(_.get(target.actualContent, 'images', []) || []) ]

            if (target.actualIndex >= 0) {
                actualImages.splice(target.actualIndex, 1, replacement)
                this.$set(target.actualContent, 'images', actualImages)

                if (!this.content) {
                    const contents = _.get(this.section, 'contents', []) || []
                    const contentIndex = contents.findIndex(content => String(content.id) === String(target.actualContent.id))
                    if (contentIndex >= 0) {
                        this.$set(contents, contentIndex, {
                            ...target.actualContent,
                            images: actualImages,
                        })
                    }
                }
            }

            if (target.shouldSyncModal) {
                const modalContent = _.cloneDeep(target.actualContent)
                const modalImage = _.get(modalContent, `images.${target.actualIndex >= 0 ? target.actualIndex : target.modalIndex}`) || replacement
                this.applyImageModalForm(this.buildImageModalForm(modalContent, modalImage, target.modalIndex))
            }

            this.bumpRenderNonce()
            this.$forceUpdate()
        },
        restoreImage(id) {
            // const img = this.getImageIndex(id)
            // if (img !== null) {
            //     store.commit(`${this.module}/form/unmarkContentImageAsDeleted`, { i: this.i, s: this.s, c: this.c, img })
            // }
        },
        // Open the unified editor (drawer mode) for a specific gallery/repeated-content
        // image. Mirrors the inline editor's wiring, but opened on click so a slider /
        // photoalbum gets the full crop + AI + versions + alt experience without stacking
        // a canvas per image. The shared onContentImage* handlers target editorDrawer.image.
        openUnifiedEditor(content, image, index = 0) {
            if (!content || !image || image.deleted) return
            this.editorDrawer = { visible: true, content, image, index }
        },
        openImageModal(id) {

            // const c = this.getContentIndex(id)
            // if (c < 0) {
            //     this.$notify.error({
            //         title: this.$t('An error occurred!'),
            //         message: this.$t('Could not find the content.'),
            //         iconClass: 'fas el-icon-fa-times',
            //         position: 'bottom-left',
            //         customClass: 'dark',
            //         duration: 8000,
            //     })
            //     return
            // }

            // let contents = []
            // if (this.module === 'menu' && this.i >= 0) {
            //     contents = _.get(this.form.menuItems[this.i].page.sections[this.s], 'contents', [])
            // } else if (this.module === 'categoryPage') {
            //     contents = _.get(this.form.sections[this.s], 'contents', [])
            // }

            // const content = _.cloneDeep(contents[c])

            const content = _.cloneDeep(this.content || this.section.contents.find(el => el.id === id));

            if (!content) {
                this.$notify.error({
                    title: this.$t('An error occurred!'),
                    message: this.$t('Could not find the content.'),
                    iconClass: 'fas el-icon-fa-times',
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 8000,
                })
                return
            }

            // const fields = _.get(content, 'fields', [])
            const images = _.get(content, 'images', [])

            if (! images.length) {
                this.$notify.error({
                    title: this.$t('An error occurred!'),
                    message: this.$t('No images found.'),
                    iconClass: 'fas el-icon-fa-times',
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 8000,
                })
                return
            }

            const i = 0
            const image = images[i]

            // check if image dimensions are too large for the viewport
            // if not, calculate the optimal

            this.imageModal.form = this.buildImageModalForm(content, image, i)
            this.imageModal.cropMoved = false
            this.selectedAspectRatioKey = 'original'
            this.customAspectRatioInput = ''
            this.altGenerateCount = this.loadAltGenerateCount(image.id)

            const altAutoKey = `alt-autogenerated-${image.id}`
            if (this.mistralEnabled && this.altAutoTriggered && !_.trim(_.get(image, 'alt', '')) && !localStorage.getItem(altAutoKey)) {
                this.altGenerating = true
                localStorage.setItem(altAutoKey, '1')
                this.generateAltTextWithMistral(image)
                    .then(alt => { if (alt) this.$set(this.imageModal.form.image, 'alt', alt) })
                    .catch(() => { localStorage.removeItem(altAutoKey) })
                    .finally(() => { this.altGenerating = false })
            }

            this.imageModal.visibility = true
            this.imageModal.loading = true
        },
        debouncedUpdateCrop: _.debounce(function() { // function scope is needed here
            this.updateCrop()
        }, 50),
        updateCrop() {
            const cropper = this.getCropperInstance()
            if (! cropper) {
                this.imageModal.form.preview = undefined
                return
            }

            this.detectAspectRatioFromCropper()

            try {
                const canvas = cropper.getCroppedCanvas()
                this.imageModal.form.preview = canvas ? canvas.toDataURL() : _.get(this.imageModal.form, 'src')
            } catch (error) {
                this.imageModal.form.preview = _.get(this.imageModal.form, 'src')
            }
        },
        updateInlineCrop(content, data, i = 0) {
            const width = _.get(content, `images.${i}.width`)
            const height = _.get(content, `images.${i}.height`)
            const processing = this.getProcessingObjectFromCropperData(data.detail, width, height)

            _.set(content, `images.${i}.settings`, { processing })
        },
        // The unified editor emits a ready-made processing object (natural focal already
        // un-flipped + rotate/flipH/flipV flags), so we store it verbatim — no recompute.
        // Preserve any other settings keys.
        storeInlineProcessing(content, processing, i = 0) {
            const image = _.get(content, `images.${i}`)
            if (!image) return
            const settings = image.settings || {}
            // $set (not _.set): when the image had no `settings`, a plain assignment adds a
            // non-reactive property, so the thumbnail's `dimensions` computed never recomputes
            // and the crop preview only appears after a full page save. $set notifies the
            // image's observer → the preview updates immediately.
            this.$set(image, 'settings', { ...settings, processing })
        },
        updateModalField({ key, value }) {
            // necessary because of nested objects
            // i.e. hyperlink
            _.set(this.imageModal.form.content, key, value);

            // TODO: check if this is still necessary
            this.$set(this.imageModal.form.content, 'tmp', uniqid())
            this.$nextTick(() => this.$delete(this.imageModal.form.content, 'tmp'))
        },
        // Setting-type fields rendered in the unified drawer's content slot update the
        // SPECIFIC clicked content (editorDrawer.content), not the legacy modal's content.
        // Regular fields mutate their bound `:content` directly, so they need no handler.
        updateContentSettings(content, { key, value }) {
            if (!content) return
            const settings = (content.settings || []).slice()
            const index = settings.findIndex(setting => setting.key === key)
            if (index >= 0) settings[index] = { ...settings[index], key, value }
            else settings.push({ key, value })
            this.$set(content, 'settings', settings)
        },
        // eslint-disable-next-line
        updateSettings({ field, key, value }) {
            let content = _.cloneDeep(this.imageModal.form.content)
            let settings = content.settings || []

            if (settings && settings.length) {
                // find by key
                const i = _.findIndex(settings, { key })
                if (i >= 0) settings[i].value = value
                else settings.push({ key, value })
            } else {
                settings = []
                settings.push({ key, value })
                content.settings = settings
            }

            this.imageModal.form.content = content
        },
        resetImageModal() {
            this.imageModal.form = {
                orientation: undefined,
                dimensions: {},
                crop: {},
                // c: undefined,
                content: {},
                image: undefined,
                src: undefined,
                preview: undefined,
                i: 0,
            }
            this.selectedAspectRatioKey = 'original'
            this.customAspectRatioInput = ''
        },
        closeImageModal() {
            this.imageModal.visibility = false
        },
        markCropMoved() {
            this.imageModal.cropMoved = true
        },
        acceptImageModal() {
            const width = _.get(this.imageModal.form, 'image.width')
            const height = _.get(this.imageModal.form, 'image.height')
            const cropper = this.getCropperInstance()
            if (! cropper) return

            const processing = this.getProcessingObjectFromCropperData(cropper.getData(true), width, height)
            _.set(this.imageModal.form, `content.images.${this.imageModal.form.i}.settings`, { processing })

            // const payload = {
            //     i: this.i,
            //     s: this.s,
            //     c: this.imageModal.form.c,
            //     content: this.imageModal.form.content,
            // }
            // store.commit(`${this.module}/form/updateContent`, payload)

            if (!this.content) {
                let contents = _.get(this.section, 'contents') || [];
                const i = contents.findIndex(el => el.id === this.imageModal.form.content.id)
                if (i >= 0) this.$set(contents, i, this.imageModal.form.content)
            } else {
                this.content = this.imageModal.form.content
            }

            this.closeImageModal()
        },
        async upoloadChanged(file, fileList) {
            if (fileList.length) this.$set(this.section, 'locked', true)
            return await this.updateUploadHeaders()
        },
        async updateUploadHeaders() {
            // The computed uploadHeaders reads the store token; make sure it is fresh.
            await ensureFreshToken()
        },
        async validateUpload(file) {
            await this.updateUploadHeaders()
            if (file.size / 1024 / 1024 > this.maxSize) {
                this.$notify.error({
                    title: this.$t('upload.sizeExceeded'),
                    message: this.$t('upload.max', { size: `${this.maxSize}mb` }),
                    iconClass: 'fas el-icon-fa-times',
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 8000,
                })
                // Reject, never `return false`: an async before-upload always returns a promise,
                // and Element aborts only on rejection - a resolved `false` uploads anyway.
                return Promise.reject()
            } else {
                this.$set(this.section, 'locked', true)
                return true
            }
        },
        async uploadSucceeded(response, file, fileList) {
            let image = _.cloneDeep(response)
            image.upload = true

            delete image.hash
            delete image.dimensions
            if ( typeof image.size === 'number' ) image.size = image.size.toString()

            // const storageRoute = _.trim(`${process.env.VUE_APP_API_ENDPOINT}${process.env.VUE_APP_API_PRIVATE_STORAGE_ROUTE}`, '/')
            // const table = 'uploads'

            // const token = AuthApi.getToken()

            // let img = new Image()
            // img.src = `${storageRoute}/${table}/${image.id}/${image.name}?token=${token}`
            // img.onload = () => {
            //     image.width = img.width
            //     image.height = img.height

            //     const payload = {
            //         i: this.i,
            //         p: this.p,
            //         s: this.s,
            //         config: this.config,
            //         content: {
            //             key: this.config.key,
            //             images: [ image ],
            //         }
            //     }
            //     if (this.c !== false && this.c >= 0 ) {
            //         store.commit(`${this.module}/form/addImageToContent`, {
            //             i: this.i,
            //             p: this.p,
            //             s: this.s,
            //             c: this.c,
            //             image
            //         })
            //     } else {
            //         store.commit(`${this.module}/form/addContent`, payload)
            //     }
            // }

            // use dimansions to create a skeleton quicker than onload
            image.width = response.dimensions.width
            image.height = response.dimensions.height
            this.applyDefaultImageAlt(image)

            // const payload = {
            //     i: this.i,
            //     p: this.p,
            //     s: this.s,
            //     config: this.config,
            //     content: {
            //         key: this.config.key,
            //         // position: _.get(this.section, 'contents.length', 0) + 1,
            //         position: file.uid,
            //         images: [ image ],
            //     }
            // }

            if (this.content) {
                // store.commit(`${this.module}/form/addImageToContent`, {
                //     i: this.i,
                //     p: this.p,
                //     s: this.s,
                //     c: this.c,
                //     image
                // })

                if (!this.content.images) this.$set(this.content, 'images', []);
                const length = this.content.images.length;
                this.$set(this.content.images, length, image);

                if (this.mistralEnabled && this.altAutoTriggered && !_.trim(image.alt)) {
                    const pushed = this.content.images[length]
                    this.$set(pushed, '_altLoading', true)
                    this.altBatchPending += 1
                    this.generateAltTextWithMistral(pushed)
                        .then(alt => { if (alt) { this.$set(pushed, 'alt', alt); if (this.altLastTokens) this.$set(this.altTokensMap, pushed.id, this.altLastTokens); this.notifyAltBatchDone(pushed.preferred_name || pushed.name) } else { this.altBatchPending -= 1 } })
                        .catch(() => { this.altBatchPending -= 1 })
                        .finally(() => { this.$delete(pushed, '_altLoading') })
                }
            } else {
                // store.commit(`${this.module}/form/addContent`, payload)
                const content = {
                    key: this.config.key,
                    // position: _.get(this.section, 'contents.length', 0) + 1,
                    position: file.uid,
                    images: [ image ],
                }
                const section = addContentToSection(this.section, this.config, content)

                if (this.mistralEnabled && this.altAutoTriggered && !_.trim(image.alt)) {
                    this.$set(image, '_altLoading', true)
                    this.altBatchPending += 1
                    this.generateAltTextWithMistral(image)
                        .then(alt => { if (alt) { this.$set(image, 'alt', alt); if (this.altLastTokens) this.$set(this.altTokensMap, image.id, this.altLastTokens); this.notifyAltBatchDone(image.preferred_name || image.name) } else { this.altBatchPending -= 1 } })
                        .catch(() => { this.altBatchPending -= 1 })
                        .finally(() => { this.$delete(image, '_altLoading') })
                }
            }

            this.$nextTick(() => {
                let contents = _.get(this.section, 'contents') || [];
                if (this.$refs.upload !== undefined) {
                    this.$refs.upload.uploadFiles = this.$refs.upload.uploadFiles.filter(el => el.uid !== file.uid)
                    if (! this.$refs.upload.uploadFiles.length) {
                        // store.commit(`${this.module}/sortContents`, { i: this.i, s: this.s, contents: this.contents })
                        sortContents(contents)
                        this.$delete(this.section, 'locked', false)
                    }
                } else {
                    // store.commit(`${this.module}/sortContents`, { i: this.i, s: this.s, contents: this.contents })
                    sortContents(contents)
                    this.$delete(this.section, 'locked', false)
                }
            })

            return await this.updateUploadHeaders()
        },
        async uploadFailed(err, file) {
            await this.updateUploadHeaders()

            this.$notify.error({
                title: this.$t('upload.notUploaded'),
                message: _.get(file, 'name'),
                iconClass: 'fas el-icon-fa-times',
                position: 'bottom-left',
                customClass: 'dark',
                duration: 8000,
            })
        },

        async getImageAsDataUrl(imageUrl, maxWidth) {
            const response = await axios.get(imageUrl, { responseType: 'blob' })
            return new Promise((resolve, reject) => {
                const objectUrl = URL.createObjectURL(response.data)
                const img = new Image()
                img.onload = () => {
                    URL.revokeObjectURL(objectUrl)
                    const canvas = document.createElement('canvas')
                    let w = img.naturalWidth
                    let h = img.naturalHeight
                    if (maxWidth && w > maxWidth) {
                        h = Math.round(h * maxWidth / w)
                        w = maxWidth
                    }
                    canvas.width = w
                    canvas.height = h
                    canvas.getContext('2d').drawImage(img, 0, 0, w, h)
                    resolve(canvas.toDataURL('image/jpeg', 0.85))
                }
                img.onerror = () => { URL.revokeObjectURL(objectUrl); reject(new Error('load failed')) }
                img.src = objectUrl
            })
        },
        async generateAltTextWithMistral(image, width = parseInt(process.env.VUE_APP_MISTRAL_ALT_WIDTH_IMAGE_INITIAL || '512', 10)) {
            const apiKey = process.env.VUE_APP_MISTRAL_ALT_API_KEY
            if (!apiKey) return ''
            const rawUrl = this.getImageSrc(image)
            let imageData = rawUrl
            try {
                imageData = await this.getImageAsDataUrl(rawUrl, width)
            } catch (e) {
                // fallback to direct URL
            }
            const { data } = await axios.post(
                process.env.VUE_APP_MISTRAL_ALT_API_URL || 'https://api.mistral.ai/v1/chat/completions',
                {
                    model: process.env.VUE_APP_MISTRAL_ALT_MODEL || 'pixtral-12b-2409',
                    messages: [{
                        role: 'user',
                        content: [
                            { type: 'image_url', image_url: imageData },
                            { type: 'text', text: 'Erstelle einen prägnanten, beschreibenden Alt-Text für dieses Bild auf Deutsch. Maximal 125 Zeichen. Nur den Alt-Text, keine weiteren Erklärungen.' }
                        ]
                    }]
                },
                { headers: { Authorization: `Bearer ${apiKey}`, 'Content-Type': 'application/json' } }
            )
            this.altLastTokens = { input: _.get(data, 'usage.prompt_tokens', '?'), output: _.get(data, 'usage.completion_tokens', '?') }
            return _.get(data, 'choices[0].message.content', '').trim()
        },
        initInlineAltGenerateCounts() {
            const allImages = []
            if (this.content) {
                allImages.push(...(_.get(this.content, 'images') || []))
            } else {
                const contents = _.get(this.section, 'contents') || []
                contents.forEach(c => allImages.push(...(_.get(c, 'images') || [])))
            }
            allImages.forEach(image => {
                if (!image.id) return
                const count = this.loadAltGenerateCount(image.id)
                if (count > 0) this.$set(this.altGenerateCounts, image.id, count)
            })
        },
        loadAltGenerateCount(imageId) {
            if (!imageId) return 0
            return parseInt(localStorage.getItem(`mistral:alt:${imageId}`) || '0', 10)
        },
        saveAltGenerateCount(imageId, count) {
            if (!imageId) return
            localStorage.setItem(`mistral:alt:${imageId}`, String(count))
        },
        async regenerateAlt() {
            if (!this.imageModal.form.image) return
            this.altGenerating = true
            this.altGenerateCount += 1
            const imageId = _.get(this.imageModal.form, 'image.id')
            this.saveAltGenerateCount(imageId, this.altGenerateCount)
            try {
                const retryWidth = this.altGenerateCount <= 1
                    ? parseInt(process.env.VUE_APP_MISTRAL_ALT_WIDTH_IMAGE_RETRY || '512', 10)
                    : parseInt(process.env.VUE_APP_MISTRAL_ALT_WIDTH_IMAGE_RETRY_MAX || '700', 10)
                const alt = await this.generateAltTextWithMistral(this.imageModal.form.image, retryWidth)
                if (alt) this.$set(this.imageModal.form.image, 'alt', alt)
                if (this.altGenerateCount >= this.altMaxGenerations) this.$notify({ title: this.$t('alt_limit_reached_notify'), type: 'warning', position: 'bottom-left', customClass: 'dark', duration: 5000 })
            } catch (e) {
                this.altGenerateCount -= 1
                this.saveAltGenerateCount(imageId, this.altGenerateCount)
                this.$notify.error({ title: this.$t('Alt text could not be generated.'), position: 'bottom-left', customClass: 'dark', duration: 5000 })
            } finally {
                this.altGenerating = false
            }
        },
        // (removed) regenerateInlineAlt — the inline alt panel it served was replaced by
        // the unified editor's shared AltTextField, which owns manual/auto generation.
        notifyAltBatchDone(imageName) {
            this.altBatchCompleted += 1
            this.altBatchPending -= 1
            if (this.altBatchPending === 0) {
                const count = this.altBatchCompleted
                this.altBatchCompleted = 0
                this.$notify({
                    title: this.$t('alt_generated_notify'),
                    message: count === 1
                        ? `<i class="el-icon-fa-magic fas"></i> ${imageName}`
                        : `<i class="el-icon-fa-magic fas"></i> ${count} ${this.$t('alt_generated_batch')}`,
                    dangerouslyUseHTMLString: true,
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 4000,
                })
            }
        },
        calculateMaxWidth() {
            if (this.$refs.container) {
                const style = getComputedStyle(this.$refs.container)
                this.maxWidth = this.$refs.container.clientWidth
                this.maxWidth -= parseFloat(style.paddingLeft) + parseFloat(style.paddingRight)
            }

            setTimeout(() => {
                if (this.edit && this.$refs.cropper) {
                    if (this.$refs.cropper.length) {
                        this.$refs.cropper.forEach(cropper => {
                            if (cropper.cropper) {
                                // cropper.cropper.initContainer()
                                // cropper.cropper.initCanvas()
                                cropper.cropper.reset()
                            }
                        })
                    } else if (this.$refs.cropper.cropper) {
                        // this.$refs.cropper.cropper.initContainer()
                        // this.$refs.cropper.cropper.initCanvas()
                        this.$refs.cropper.cropper.reset()
                    }
                }
            }, 300)
        }
    },
    mounted() {
        window.addEventListener('resize', _.debounce(this.calculateMaxWidth, 100))
        this.calculateMaxWidth()
        this.$nextTick(() => this.initInlineAltGenerateCounts())
    },
    destroyed() {
        window.removeEventListener('resize', _.debounce(this.calculateMaxWidth, 100))
    }
}
</script>

<style lang="sass" scoped>
// Content input fields injected into the unified editor's content slot — separate them
// from the image/alt area above with space and a light divider.
.gm-content-fields
    margin-top: 24px
    padding-top: 20px
    border-top: 1px solid #ebeef5
    .field
        margin-bottom: 16px
        &:last-child
            margin-bottom: 0

#body .el-form-item.flex
    &::v-deep
        .el-form-item__content
            display: flex
            flex-direction: column
            gap: 6px
            &:before
                display: none
            & > .list,
            & > .list > ul.no-style-list
                display: flex !important
                flex-wrap: wrap
                gap: 6px
            .dropzone-block
                width: 100%

            .replace-action
                flex-basis: 100%
                margin-bottom: 8px
                display: flex
                align-items: center
                justify-content: space-between
                gap: 8px
                .aspect-ratio-group
                    display: flex
                    align-items: center
                    gap: 8px
                &::v-deep .el-dropdown-menu__item.is-selected
                    color: $--color-primary
                    font-weight: 600
    &.inline-cropper::v-deep
        .cropper-box
            padding: 0
        .setting .select,
        .quill-editor
            width: 100%
        li.image,
        li.content
            flex-basis: 100%
            // Flex items default to min-width:auto, so these could never shrink below
            // their content. The image editor inside needs ~914px (canvas min-content +
            // the 300px AI rail) while this drawer pane offers ~848, and the difference
            // escaped as a horizontal scrollbar across the entire drawer rather than the
            // editor fitting the space it has.
            min-width: 0
        ul.no-style-list
            min-width: 0

.list
    margin-bottom: 10px

.image-actions
    margin-bottom: 10px
    padding: 12px
    border: 1px solid #dce6f8
    border-radius: 8px
    background: #f5f8ff
    &--replace
        display: flex
        justify-content: flex-start
        padding: 0
        border: 0
        border-radius: 0
        background: transparent
    &--inline
        margin-bottom: 10px

.image-library-button
    width: 100%
    min-height: 44px
    font-weight: 600
    &::v-deep span
        white-space: normal
    &--replace
        width: auto
        min-height: 0
        font-weight: 500

.image-actions__hint
    margin-top: 8px
    font-size: 12px
    line-height: 1.45
    color: #6b7280

.image-dialog-actions
    display: flex
    justify-content: flex-start
    align-items: center
    margin-bottom: 12px
    gap: 8px
    .aspect-ratio-group
        display: flex
        align-items: center
        gap: 8px
    &::v-deep .el-dropdown-menu__item.is-selected
        color: $--color-primary
        font-weight: 600

.aspect-ratio-control
    display: flex
    align-items: center
    gap: 6px

.aspect-ratio-toolbar
    display: flex
    align-items: center
    justify-content: flex-end
    gap: 6px
    margin-bottom: 8px
    &::v-deep .el-dropdown-menu__item.is-selected
        color: $--color-primary
        font-weight: 600

.form .field.edit:not(:last-child)
    margin-bottom: 22px

.cropper-box
    overflow: hidden

.alternate-text
    position: relative
    z-index: 1

.images.is-dragging
    li.content button.el-button
        display: none
    li.dropzone-inline
        // display: none
        pointer-events: none
        opacity: .3

.images.preview
    margin-bottom: 10px
    ul > li:only-child
        margin-bottom: 0

.alt-text-feature
    border: 1px solid #ddd6fe
    border-radius: 10px
    background: linear-gradient(140deg, #faf8ff 0%, #eff0ff 100%)
    padding: 12px 14px
    width: 100%
    margin-bottom: 16px

    &--plain
        background: none
        border: none
        border-radius: 0
        padding: 0
        .alt-text-feature__header
            margin-bottom: 8px
        .alt-text-feature__label
            color: #606266
            font-size: 14px
            font-weight: 400
            i
                display: none
        .alt-text-feature__input::v-deep .el-input__inner
            border-color: #dcdfe6
            &:focus
                border-color: #409eff
                box-shadow: none

    &__header
        display: flex
        align-items: center
        justify-content: space-between
        margin-bottom: 10px

    &__label
        display: flex
        align-items: center
        gap: 6px
        font-size: 12px
        font-weight: 600
        color: #5b4de8
        i
            font-size: 11px
            margin-right: 4px

    &__meta
        display: flex
        align-items: center
        gap: 6px

    &__badge
        font-size: 10px
        font-weight: 700
        letter-spacing: 0.5px
        background: #5b4de8 !important
        border-color: #5b4de8 !important
        color: #fff !important
        border-radius: 4px
        padding: 0 5px

    &__help
        padding: 0
        height: 20px
        width: 20px
        min-height: auto
        min-width: auto !important
        font-size: 11px
        font-weight: 600
        background: #ede9fe
        border-color: #c4b5fd
        color: #5b4de8

    &__input
        &::v-deep .el-input__inner
            border-color: #c4b5fd
            border-radius: 6px
            background: #fff
            &:focus
                border-color: #5b4de8
                box-shadow: 0 0 0 2px rgba(91, 77, 232, 0.1)

    &__footer
        margin-top: 10px
        display: flex
        flex-direction: column
        gap: 6px

    &__btn
        align-self: flex-start
        background: #5b4de8
        border-color: #5b4de8
        color: #fff
        font-weight: 600
        border-radius: 6px
        &:hover, &:focus
            background: #4c3dd3
            border-color: #4c3dd3
            color: #fff

    &__hint
        margin: 0
        font-size: 11px
        color: #9ca3af
        line-height: 1.4

.flex-fields
    display: flex
    .input
        flex-grow: 1
    .comment
        .tooltip
            margin-left: 0.5rem
            padding-top: 23px
            .el-button
                padding: 0
                height: 36px
                width: 36px
                line-height: 20px
                text-align: center
                font-weight: 500
                background: #f9fafc
                &:focus, &:active
                    border-color: #dde0e6
                    color: #606266


.edit-popover
    position: absolute
    inset: 0
    display: flex
    align-items: center
    justify-content: center
    font-size: 1.2rem
    color: #fff
    background-color: rgba(0, 0, 0, 0.3)
    backdrop-filter: grayscale(1)
    border-radius: 4px
    opacity: 0
    transition: opacity .15s ease

.images ul
    margin: 0
    display: flex
    flex-wrap: wrap
    gap: 6px
    & + .plus-box
        margin-top: 10px
    & > li
        // &:only-child
        //     margin-bottom: 0
        a
            display: block
            position: relative
            &::v-deep img
                transition: .15s filter ease, .15s opacity ease
            &.deleted
                cursor: default
                &::v-deep img
                    opacity: .6
                    filter: grayscale(1) blur(1px)
    .image-input
        border-radius: 3px
        border: 1px solid $--border-color-base
        margin-bottom: 0
        display: block
        // height: 150px
        // margin-right: 6px
    & > li > a,
    &::v-deep .thumbnail
        position: relative
        height: 100%
        .el-button.delete-image,
        .el-button.restore-image
            position: absolute
            top: 5px
            right: 5px
            z-index: 2
            // background: rgba(0, 0, 0, 0.2)
            // border: none
            // text-shadow: 0px 0px 5px black
            width: 30px
            height: 30px
            opacity: 0
            transition: opacity .15s ease

            // &:hover
            //     background: rgba(0, 0, 0, 0.6)
        &:hover
            .edit-popover,
            .el-button.delete-image,
            .el-button.restore-image
                opacity: 1

        img
            width: auto
            height: 100%
            display: block
            border-radius: 3px

.image
    display: block
    // height: 80px
    &::v-deep .thumbnail
        position: relative
        height: 100%
        // margin-right: 6px
        img
            width: auto
            height: 100%
            display: block
            border-radius: 3px
            border: 1px solid $--border-color-base

.inline-cropper
    .cropper-box
        width: 100%
        // height: 40vh
        &::v-deep
            img
                max-height: 40vh

.image-dialog .content-sidebar
    padding-bottom: 16px

.image-dialog .content-sidebar .form
    margin-bottom: 22px
.el-form-item.no-margin
    margin-bottom: 0
</style>
